import "./ImageHalfWidthBlock.scss";
import { ImageHalfWidthBlockProps } from "/Models/Generated/ImageHalfWidthBlockProps";
import {
  Picture,
  ArrowsOutSimple,
  Video,
  VideoPlayButton,
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
} from "/App.Components";
import { createSpreadableBem } from "/Helpers/createBem";
import { useModal } from "/Components/Shared/ModalProvider/ModalProvider";
import { RefObject, useEffect, useRef, useState } from "react";
import FillViewportKeepAspectRatio from "/Helpers/FillViewportKeepAspectRatio";

interface Props extends ImageHalfWidthBlockProps {
  insideModal?: boolean;
  source?: RefObject<HTMLDivElement | null>;
  startTime?: number;
  autoPlay?: boolean;
  initialSlideIndex?: number;
}

const ImageHalfWidthBlock = (props: Props) => {
  const {
    video,
    descriptionText,
    insideModal,
    source,
    zoomImage,
    zoomButtonAriaLabel,
    carouselImages,
    zoomedCarouselImages,
    previousButtonAriaLabel,
    nextButtonAriaLabel,
    startTime,
    autoPlay = false,
    initialSlideIndex = 0,
  } = props;
  const { openModal } = useModal();
  const [videoIsPlaying, setVideoIsPlaying] = useState(!!autoPlay);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(initialSlideIndex);
  const hasVideo = !!video?.videoUrl;

  const regularSlides =
    (carouselImages ?? []).length > 0 ? (carouselImages ?? []) : [];

  const publishedSlides = regularSlides;

  const slides =
    insideModal && publishedSlides.length > 0
      ? publishedSlides.map(
          (slide, idx) =>
            zoomedCarouselImages?.[idx] ??
            (idx === 0 ? zoomImage : undefined) ??
            slide,
        )
      : publishedSlides;

  const hasCarousel = !hasVideo && publishedSlides.length > 1;
  const currentImage = !hasVideo && slides.length > 0 ? slides[currentSlideIndex] : zoomImage;
  const showZoomControl = !insideModal;

  const thisRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!insideModal || !thisRef.current || !source?.current) return;
    const adjustWidth = () =>
      FillViewportKeepAspectRatio(62, 40, thisRef, source);
    adjustWidth();
    window.addEventListener("resize", adjustWidth);
    return () => window.removeEventListener("resize", adjustWidth);
  }, []);

  useEffect(() => {
    setCurrentSlideIndex(initialSlideIndex);
  }, [initialSlideIndex]);

  useEffect(() => {
    if (currentSlideIndex > Math.max(publishedSlides.length - 1, 0)) {
      setCurrentSlideIndex(0);
    }
  }, [publishedSlides.length, currentSlideIndex]);

  const goToPreviousSlide = () => {
    if (!hasCarousel) return;
    setCurrentSlideIndex((prev) =>
      prev === 0 ? publishedSlides.length - 1 : prev - 1,
    );
  };

  const goToNextSlide = () => {
    if (!hasCarousel) return;
    setCurrentSlideIndex((prev) =>
      prev === publishedSlides.length - 1 ? 0 : prev + 1,
    );
  };

  const handleVideoPlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setVideoIsPlaying(true);
    }
  };

  const handleVideoPause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleZoom = () => {
    handleVideoPause();
    const currentTime = videoRef.current?.currentTime ?? 0;
    openModal(
      <div {...bem("__modal-wrapper")}>
        <ImageHalfWidthBlock
          {...props}
          insideModal
          source={thisRef}
          startTime={currentTime}
          autoPlay={true}
          video={hasVideo ? video : undefined}
          initialSlideIndex={currentSlideIndex}
        />
      </div>
    );
  };

  const bem = createSpreadableBem("image-half-width-block-norad");
  return (
    <div {...bem("", { "--inside-modal": insideModal })} ref={thisRef}>
      {hasVideo ? (
        video?.videoUrl && (
          <div {...bem("__video-wrapper")}>
            <Video
              {...video}
              videoUrl={video.videoUrl}
              preload={autoPlay ? "auto" : "metadata"}
              aspect="9/16"
              ref={videoRef}
              startTime={startTime}
              autoPlay={autoPlay}
              poster={!autoPlay ? currentImage?.srcSet?.[0]?.srcSet : undefined}
              controls={videoIsPlaying}
            />
            {!videoIsPlaying && (
              <button {...bem("__play-button")} onClick={handleVideoPlay}>
                <VideoPlayButton width="48" height="48" />
              </button>
            )}
          </div>
        )
      ) : currentImage ? (
        <div {...bem("__thumbnail")}>
          <Picture
            {...currentImage}
            pictureAspectRatio="4/5"
            pictureBehaviour="Cover"
          />
        </div>
      ) : null}
          <div {...bem("__caption")}>
              <div {...bem("__caption-main")}>
                  {descriptionText && <p {...bem("__description")}>{descriptionText}</p>}
                  <div {...bem("__copyright-and-zoom")}>
                      {currentImage
                          ? currentImage.copyrightLabel &&
                          currentImage.copyright && (
                              <span {...bem("__copyright")}>
                                  {`${currentImage?.copyrightLabel ?? ""} ${currentImage.copyright}`}
                              </span>
                          )
                          : hasVideo && video
                              ? video.copyrightLabel &&
                              video.copyright && (
                                  <span {...bem("__copyright")}>
                                      {`${video?.copyrightLabel ?? ""} ${video.copyright}`}
                                  </span>
                              )
                              : null}
                  </div>
              </div>
              {showZoomControl && (
                  <div {...bem("__zoom-section")}>
                      <button
                          {...bem("__zoom")}
                          aria-label={zoomButtonAriaLabel}
                          onClick={handleZoom}
                      >
                          <span>
                              <ArrowsOutSimple size="20" thickness="Reg" />
                          </span>
                      </button>
                  </div>
              )}
          </div>
          {hasCarousel && (
              <div {...bem("__carousel-controls")}>
                  <div {...bem("__carousel-controls-inner")}>
                      <button
                          type="button"
                          {...bem("__carousel-prev")}
                          onClick={goToPreviousSlide}
                          aria-label={previousButtonAriaLabel}
                      >
                          <CaretCircleLeftIcon size="32" thickness="Light" color="#5B0337" />
                      </button>
                      <div {...bem("__dots")}>
                          {publishedSlides.map((_, idx) => (
                              <button
                                  key={idx}
                                  type="button"
                                  {...bem("__dot", {
                                      "--active": idx === currentSlideIndex,
                                  })}
                                  aria-label={`${idx + 1}`}
                              aria-current={idx === currentSlideIndex ? "true" : undefined}
                              disabled={idx === currentSlideIndex}
                              tabIndex={idx === currentSlideIndex ? -1 : 0}
                                  onClick={() => setCurrentSlideIndex(idx)}
                              />
                          ))}
                      </div>
                      <button
                          type="button"
                          {...bem("__carousel-next")}
                          onClick={goToNextSlide}
                          aria-label={nextButtonAriaLabel}
                      >
                          <CaretCircleRightIcon size="32" thickness="Light" color="#5B0337" />
                      </button>
                  </div>
          </div>
        )}
      </div>
  );
};

export default ImageHalfWidthBlock;
